import React, { useEffect, useRef } from 'react'
import parse from 'html-react-parser'
import * as E from '../../elementor'
import AniLink from 'gatsby-plugin-transition-link/AniLink'
import { EmbedCode } from '../EmbedCode'
import Logo from '../../images/rula_black.svg'
import { v4 as uuidv4 } from 'uuid'
import EmailCapture from '../email-capture'
import RulaBadge from "@pathccm/rula-ui/badge";
import DownloadInHero from '../download-in-hero'
import { useFlags } from 'gatsby-plugin-launchdarkly'
import { EventPrefix, Trigger } from '@pathccm/common-analytics/events'
import { getReportingAdapter } from '../../reporting/get-reporting-adapter'

interface ContentOverride {
  logo?: string
  heading?: string
  subheading?: string
  form?: string
  bulletPoints?: string
}

interface Props {
  section?: E.Section
  theme?: 'sky' | 'fern' | 'amber' | 'amethist' | 'amethist-gradient'| 'sky-gradient'
  contentOverride?: ContentOverride
  disableFreshpaint?: boolean // Does not submit any form info to freshpaint
  downloadCaptureSection?: E.Section
  heroDownload?: E.Section,
  showPill?: boolean
}

interface freshpaint {
  track: Function
}

declare const freshpaint: freshpaint

type BulletPointProps = {
  items: string;
}

function BulletPoints({items}: BulletPointProps) {
  const bulletList: string[] = items.split(",").map(item => item.trim())
  const listItems = bulletList.map((item) => <li>{item}</li>)
  return <ul>{listItems}</ul>
}

const ExperimentAHero: React.FC<Props> = ({ section, contentOverride, theme = 'sky', disableFreshpaint, downloadCaptureSection, heroDownload, showPill }) => {
  const { heading = [], textEditor = [], testimonial = [], html = [], image = [] } = (section != null) ? E.collect(section) : {}

  const headingText = contentOverride?.heading ?? heading[0]?.title ?? ''
  const subheadingText = contentOverride?.subheading ?? heading[1]?.title ?? ''
  const partnerLogo = contentOverride?.logo ?? image[0]?.image?.fileUrl ?? ''
  const embedForm = contentOverride?.form ?? html[0]?.html ?? ''
  const bulletPoints = contentOverride?.bulletPoints ?? ''
  const cssClasses = section?.cssClasses ?? ''

  const tpTestimonial = testimonial[0]?.testimonials[0]
  const formRef = useRef(null)
  const uuid: string = uuidv4()
  const insuranceExclusionList = ['I Don’t Plan to Use Insurance', "I Don't See Mine", 'Blue Cross Blue Shield', 'Empire Blue Cross Blue Shield']

  const rawText = textEditor[0]?.parse() ?? []
  // Ensure `text` is always an array
  const text = Array.isArray(rawText) ? rawText : [rawText]

  // Split the text into two sections:
  // Paragraphs will sit below the form on browser, but above the form on mobile
  // Everything else will sit below the form on browser and mobile
  let textAbove: Array<string | JSX.Element | null> = []
  let textBelow: Array<string | JSX.Element | null> = []

  const { marketingSiteSegmentTransition } = useFlags()
  const reporting = getReportingAdapter(marketingSiteSegmentTransition)

  if (cssClasses == 'list-below') {
    text.forEach((element) => {
      if (typeof element === 'object' && element?.type === 'p') {
        textBelow.push(element)
      } else {
        textAbove.push(element)
      }
    })
  } else {
    text.forEach((element) => {
      if (typeof element === 'object' && element?.type === 'p') {
        textAbove.push(element)
      } else {
        textBelow.push(element)
      }
    })
  }

  // If custom bullet points are provided, replace the default bullet points
  if (bulletPoints !== '') {
    const bulletPointsElement: JSX.Element = <BulletPoints items={bulletPoints} />

    if (textBelow.length !== 0) {
      textBelow = []
      textBelow.push(bulletPointsElement)
    } else {
      textAbove = []
      textAbove.push(bulletPointsElement)
    }
  }

  const getElementValue = (id: string): string => {
    const element = document.getElementById(id) as HTMLInputElement | HTMLSelectElement
    return element?.value ?? null
  }

  const checkIsMinor = (): boolean => {
    const birthdate = getElementValue('Contact.Birthdate')
    const date = new Date(birthdate)
    const diff = Date.now() - date.getTime()
    const diffDate = new Date(diff)
    const year = diffDate.getUTCFullYear()
    const age = Math.abs(year - 1970)
    const isMinor = Boolean(age < 18)

    return isMinor
  }

  const submitAnalyticsToFreshpaint = (): void => {
    const minor = checkIsMinor()

    const selectedLocation = getElementValue('Contact.Patient_Location__c')

    const selectedInsurance = getElementValue('Contact.Insurance_Carrier__c')

    const therapyInput = document?.querySelector('[data-ff-ext-radio-checked = "true"]') as HTMLSelectElement
    const selectedTherapyType = therapyInput?.getAttribute('data-value')

    const eventData = {
      event: `upsert_${uuid}`,
      minor,
      insurance: selectedInsurance,
      location: selectedLocation,
      therapyType: selectedTherapyType
    }

    if (!insuranceExclusionList.includes(selectedInsurance)) {
      freshpaint.track('patient_insurance_conversion', eventData)
    }
  }

  // check the form wrapper elements, set a click event listener on the 'Next' button to update values
  const checkElements = (mutationsList: MutationRecord[]): void => {
    for (const mutation of mutationsList) {
      if (mutation.type === 'childList') {
        const addedNodes = mutation.addedNodes
        for (const node of addedNodes) {
          if (node instanceof Element && node.id === 'btnnext' && marketingSiteSegmentTransition) {
            const submitButton = document.getElementById('btnsubmit') as HTMLButtonElement
            submitButton?.addEventListener('click', () => {
              const email = getElementValue('Contact.Email')
              reporting.track({
                eventName: {
                  prefix: EventPrefix.MARKETING_SITE,
                  trigger: Trigger.FORM_SUBMITTED
                },
                properties: {
                  email
                }
              })
            })
            if (!disableFreshpaint) {
              submitButton?.addEventListener('click', submitAnalyticsToFreshpaint)
            }
          }
        }
      }
    }
  }

  useEffect(() => {
    const observer = new MutationObserver(checkElements)
    const config = { childList: true, subtree: true }
    const formWrapper = formRef.current;
    (formWrapper != null) && observer.observe(formWrapper, config)
    return () => {
      observer.disconnect()
    }
  }, [marketingSiteSegmentTransition])

  return (
    <header className={`embedded-form-hero ${theme} ${cssClasses}`}>
      <div className='rula-logo-wrapper'><AniLink aria-label='Home' fade to='/'><img src={Logo} alt='Rula logo' className='rula-logo' /></AniLink></div>
      <div className='hero-content-wrapper'>
        <div className='heading-container' id='section-half-and-half'>
          {partnerLogo && <img src={partnerLogo} alt='Insurance logo' className='insurance-logo' />}
          {showPill &&
            <RulaBadge style="Info" size="large" className="embedded-hero-badge">
              {parse(textEditor[1]?.text() || "")}
            </RulaBadge>
          }
          <h1 className='hero-headline'>
            {parse(headingText)}
          </h1>
          {subheadingText
            ? <h2 className='hero-subheader'>
              {parse(subheadingText)}
              </h2>
            : null}
        </div>
        <p className={`paragraph hero-features features-above ${textAbove.length === 0 ? 'empty' : ''}`}>
          {textAbove}
        </p>
        <p className='paragraph hero-features features-below'>
          {textBelow}
        </p>
        {tpTestimonial && (
          <div className='testimonial-wrapper'>
            {tpTestimonial?.image !== undefined && <img className='testimonial-image' src={tpTestimonial?.image?.url} alt='5 stars' />}
            <p className='testimonial-content subhead s5'>{tpTestimonial?.content}</p>
            <p className='testimonial-name paragraph-bold'>{tpTestimonial?.name}</p>
            <p className='testimonial-rating paragraph-xsmall'>5 Stars &#183; Excellent Review</p>
          </div>
        )}
        <div className='hero-form-wrapper' ref={formRef}>
          {embedForm && <EmbedCode fragment={embedForm} />}
          {downloadCaptureSection !== undefined && <EmailCapture emailCaptureSection={downloadCaptureSection} isDownload={true}/>}
          {heroDownload !== undefined && <DownloadInHero section={heroDownload}/>}
        </div>
      </div>
    </header>
  )
}

export default ExperimentAHero
